import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Stack,
  CircularProgress,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Radio,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CustomModal from '../CustomModal';
import { createUser } from '../../services/adminService';
import { checkUserExists, getAccountLicenses, addUserLicenseForUser } from '../../services';

const STEPS = ['Create User', 'Assign License'];

const AddUserModal = ({ open, onClose, onUserCreated, allowedDomains = [] }) => {
  const theme = useTheme();
  const pollCancelRef = useRef(false);

  // Stepper
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 — Create User
  const [formData, setFormData] = useState({ email: '', name: '', phoneNumber: '' });
  const [errors, setErrors] = useState({ email: '', phoneNumber: '' });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [polling, setPolling] = useState(false);
  const [pollWarning, setPollWarning] = useState(false);

  // Step 2 — Assign License
  const [licenses, setLicenses] = useState([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [selectedLicenseID, setSelectedLicenseID] = useState(null);
  const [expirationDate, setExpirationDate] = useState('2099-12-31');
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');
  const [licensesAssigned, setLicensesAssigned] = useState(false);
  const [assignedLicenses, setAssignedLicenses] = useState([]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      pollCancelRef.current = true;
      setActiveStep(0);
      setFormData({ email: '', name: '', phoneNumber: '' });
      setErrors({ email: '', phoneNumber: '' });
      setApiError('');
      setLoading(false);
      setPolling(false);
      setPollWarning(false);
      setLicenses([]);
      setSelectedLicenseID(null);
      setExpirationDate('2099-12-31');
      setAssignError('');
      setAssignSuccess('');
      setLicensesAssigned(false);
      setAssignedLicenses([]);
    }
  }, [open]);

  // Validation
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Invalid email format';
    if (allowedDomains.length > 0) {
      const domain = email.split('@')[1]?.toLowerCase();
      if (!allowedDomains.map(d => d.toLowerCase()).includes(domain)) {
        return `Only ${allowedDomains.join(', ')} email addresses are allowed`;
      }
    }
    return '';
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(phoneNumber)) return 'Phone must be in E.164 format (e.g., +1234567890)';
    return '';
  };

  const isFormValid = () => {
    return !validateEmail(formData.email) && !validatePhoneNumber(formData.phoneNumber);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setApiError('');
    let error = '';
    if (field === 'email') error = validateEmail(value);
    else if (field === 'phoneNumber') error = validatePhoneNumber(value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Fetch active licenses for Step 2
  const fetchLicenses = async () => {
    setLicensesLoading(true);
    try {
      const result = await getAccountLicenses();
      const active = (result?.data ?? []).filter(
        (lic) => lic.IsActive?.data?.[0] === 1
      );
      setLicenses(active);
    } catch (e) {
      console.error('[AddUserModal] getAccountLicenses error:', e);
    } finally {
      setLicensesLoading(false);
    }
  };

  // Poll Aurora for user existence after Cognito creation
  const pollForUser = async (email) => {
    pollCancelRef.current = false;
    setPolling(true);
    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    // initial 5s wait for Step Function to run
    await delay(5000);

    for (let i = 0; i < 6; i++) {
      if (pollCancelRef.current) return;
      try {
        const result = await checkUserExists(email);
        if (result?.exists) {
          setPolling(false);
          setActiveStep(1);
          fetchLicenses();
          return;
        }
      } catch (e) { console.log(e) }
      if (i < 5) await delay(5000);
    }

    // Timed out after 30s — advance with warning
    if (!pollCancelRef.current) {
      setPolling(false);
      setPollWarning(true);
      setActiveStep(1);
      fetchLicenses();
    }
  };

  // Step 1 submit
  const handleCreateUser = async () => {
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhoneNumber(formData.phoneNumber);
    setErrors({ email: emailError, phoneNumber: phoneError });
    if (emailError || phoneError) return;

    setLoading(true);
    setApiError('');
    try {
      const result = await createUser(formData.email, formData.name || null, formData.phoneNumber || null);
      if (result.success) {
        setLoading(false);
        pollForUser(formData.email);
      } else {
        setApiError(result.message || 'Failed to create user');
        setLoading(false);
      }
    } catch (e) {
      setApiError(e.message || 'Failed to create user. Please try again.');
      setLoading(false);
    }
  };

  // Step 2 retry poll after timeout
  const handleRetryPoll = async () => {
    try {
      const result = await checkUserExists(formData.email);
      if (result?.exists) setPollWarning(false);
    } catch (e) { console.log(e) }
  };

  // Step 2 submit
  const handleAssignLicense = async () => {
    if (!selectedLicenseID) return;
    setLoading(true);
    setAssignError('');
    try {
      const result = await addUserLicenseForUser(formData.email, selectedLicenseID, expirationDate);
      if (result?.success) {
        const lic = licenses.find((l) => l.pkAccountLicenseID === selectedLicenseID);
        if (lic) setAssignedLicenses((prev) => [...prev, { ...lic, assignedExpiration: expirationDate }]);
        setAssignSuccess('License assigned successfully.');
        setSelectedLicenseID(null);
        setLicensesAssigned(true);
        setLoading(false);
      } else {
        setAssignError(result?.error || 'Failed to assign license');
        setLoading(false);
      }
    } catch (e) {
      setAssignError(e.message || 'Failed to assign license');
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && isFormValid() && !loading && activeStep === 0) {
      handleCreateUser();
    }
  };

  const focusSx = {
    '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#04aeea' } },
    '& .MuiInputLabel-root.Mui-focused': { color: '#04aeea' },
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Add New User"
      subtitle={activeStep === 0 ? 'Create a new user account in the system' : 'Assign an account license to the user'}
      icon={<PersonAddIcon sx={{ color: 'white', fontSize: 24 }} />}
      maxWidth="md"
      contentPadding={true}
      disableBackdropClick={true}
      actions={
        <Stack direction="row" spacing={2} sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 1 && licensesAssigned ? onUserCreated : onClose}
            disabled={loading || polling}
            sx={{
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[50],
              },
            }}
          >
            {activeStep === 1 ? 'Close' : 'Cancel'}
          </Button>

          {activeStep === 0 && (
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={!isFormValid() || loading || polling}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#04aeea',
                color: 'white',
                '&:hover': { backgroundColor: '#0398ce' },
                '&:disabled': {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.grey[500],
                },
              }}
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          )}

          {activeStep === 1 && (
            <Button
              variant="contained"
              onClick={handleAssignLicense}
              disabled={!selectedLicenseID || loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#04aeea',
                color: 'white',
                '&:hover': { backgroundColor: '#0398ce' },
                '&:disabled': {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.grey[500],
                },
              }}
            >
              {loading ? 'Assigning...' : 'Assign License'}
            </Button>
          )}
        </Stack>
      }
    >
      <Box sx={{ py: 2 }}>
        {/* Stepper header */}
        <Stepper activeStep={activeStep} sx={{
          mb: 3,
          '& .MuiStepIcon-root.Mui-active': { color: '#04aeea' },
          '& .MuiStepIcon-root.Mui-completed': { color: '#04aeea' },
        }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Create User */}
        {activeStep === 0 && (
          <>
            {polling ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                <CircularProgress sx={{ color: '#04aeea' }} />
                <Typography variant="body1" color="text.secondary">
                  Verifying user account creation...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This may take up to 30 seconds
                </Typography>
              </Box>
            ) : (
              <>
                {apiError && (
                  <Alert severity="error" onClose={() => setApiError('')} sx={{ mb: 3 }}>
                    {apiError}
                  </Alert>
                )}

                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(4, 174, 234, 0.1)'
                      : 'rgba(4, 174, 234, 0.05)',
                    '& .MuiAlert-icon': { color: '#04aeea' },
                  }}
                >
                  <Typography variant="body2">
                    A welcome email with a temporary password will be sent to the user&apos;s email address.
                  </Typography>
                </Alert>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      required
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      onKeyPress={handleKeyPress}
                      error={!!errors.email}
                      helperText={errors.email || 'User will receive login credentials here'}
                      disabled={loading}
                      autoComplete="email"
                      sx={focusSx}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      onKeyPress={handleKeyPress}
                      helperText="Optional"
                      disabled={loading}
                      autoComplete="name"
                      sx={focusSx}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phoneNumber}
                      onChange={handleChange('phoneNumber')}
                      onKeyPress={handleKeyPress}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber || 'Optional - Format: +1234567890'}
                      disabled={loading}
                      autoComplete="tel"
                      placeholder="+1234567890"
                      sx={focusSx}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}

        {/* Step 2: Assign License */}
        {activeStep === 1 && (
          <>
            {pollWarning && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button size="small" color="inherit" onClick={handleRetryPoll}>
                    Retry
                  </Button>
                }
              >
                User account is still being provisioned. License assignment may fail.
              </Alert>
            )}

            {assignSuccess && (
              <Alert severity="success" onClose={() => setAssignSuccess('')} sx={{ mb: 2 }}>
                {assignSuccess}
              </Alert>
            )}

            {assignError && (
              <Alert severity="error" onClose={() => setAssignError('')} sx={{ mb: 2 }}>
                {assignError}
              </Alert>
            )}

            {/* License selection card */}
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>

              {/* Card header */}
              <Box sx={{
                px: 2.5,
                py: 1,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(4,174,234,0.08)' : 'rgba(4,174,234,0.04)',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                <Typography variant="caption" sx={{
                  color: '#04aeea', fontWeight: 700, letterSpacing: 0.8,
                  textTransform: 'uppercase', fontSize: '0.68rem'
                }}>
                  Select a License
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Assigning to <strong style={{ color: theme.palette.text.primary }}>{formData.email}</strong>
                </Typography>
              </Box>

              {/* License table */}
              {(() => {
                const unassigned = licenses.filter(
                  (l) => !assignedLicenses.some((a) => a.pkAccountLicenseID === l.pkAccountLicenseID)
                );
                if (licensesLoading) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: '#04aeea' }} />
                    </Box>
                  );
                }
                if (licenses.length === 0) {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">No active licenses available</Typography>
                    </Box>
                  );
                }
                if (unassigned.length === 0) {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">All available licenses have been assigned</Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ px: 2.5, py: 1.5 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                            <TableCell padding="checkbox" />
                            <TableCell sx={{ fontWeight: 600 }}>Account Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {unassigned.map((lic) => (
                            <TableRow
                              key={lic.pkAccountLicenseID}
                              hover
                              selected={selectedLicenseID === lic.pkAccountLicenseID}
                              onClick={() => setSelectedLicenseID(lic.pkAccountLicenseID)}
                              sx={{
                                cursor: 'pointer',
                                '&:last-child td': { border: 0 },
                                '&.Mui-selected': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(4,174,234,0.15)'
                                    : 'rgba(4,174,234,0.08)',
                                },
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Radio
                                  checked={selectedLicenseID === lic.pkAccountLicenseID}
                                  size="small"
                                  sx={{ color: '#04aeea', '&.Mui-checked': { color: '#04aeea' } }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: selectedLicenseID === lic.pkAccountLicenseID ? 600 : 400 }}
                                >
                                  {lic.AccountName}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {lic.MetaData}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}

              {/* Expiration date footer */}
              <Box sx={{
                px: 2.5,
                py: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : theme.palette.grey[50],
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <TextField
                  label="Expiration Date"
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                  size="small"
                  helperText="Default: no expiration (2099-12-31)"
                  sx={{ width: 220, ...focusSx }}
                />
                {selectedLicenseID && (
                  <Typography variant="caption" color="text.secondary">
                    Selected:{' '}
                    <strong style={{ color: '#04aeea' }}>
                      {licenses.find((l) => l.pkAccountLicenseID === selectedLicenseID)?.AccountName}
                    </strong>
                  </Typography>
                )}
              </Box>

            </Box>

            {/* Assigned This Session */}
            {assignedLicenses.length > 0 && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
                <Box sx={{
                  px: 2.5,
                  py: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(4,174,234,0.08)' : 'rgba(4,174,234,0.04)',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="caption" sx={{
                    color: '#04aeea', fontWeight: 700, letterSpacing: 0.8,
                    textTransform: 'uppercase', fontSize: '0.68rem'
                  }}>
                    Assigned This Session
                  </Typography>
                </Box>
                <Box sx={{ px: 2.5, py: 1.5 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                          <TableCell sx={{ fontWeight: 600 }}>Account Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Expiration</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignedLicenses.map((lic) => (
                          <TableRow key={lic.pkAccountLicenseID} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{lic.AccountName}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" color="text.secondary">{lic.MetaData}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" color="text.secondary">{lic.assignedExpiration}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default AddUserModal;
